import React from 'react';
import { LegalPageLayout } from './LegalPageLayout';

const Section = ({
  number,
  title,
  children,
}: {
  number?: string;
  title: string;
  children: React.ReactNode;
}) => (
  <section className="p-6 md:p-8 rounded-3xl bg-zinc-900/40 border border-white/5 backdrop-blur-sm">
    {number && (
      <div className="flex items-center gap-3 mb-4">
        <span className="w-8 h-8 rounded-lg bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center text-emerald-400 font-bold text-sm">
          {number}
        </span>
        <h2 className="text-white font-bold text-xl md:text-2xl">{title}</h2>
      </div>
    )}
    {!number && <h2 className="text-white font-bold text-xl md:text-2xl mb-4">{title}</h2>}
    <div className="text-zinc-400 leading-relaxed text-base md:text-lg space-y-4">{children}</div>
  </section>
);

const HighlightBox = ({
  children,
  color = 'blue',
  className = '',
}: {
  children: React.ReactNode;
  color?: 'blue' | 'purple' | 'emerald' | 'amber' | 'red';
  className?: string;
}) => {
  const colors = {
    blue: 'from-blue-500/10 to-blue-500/5 border-blue-500/20 text-blue-400',
    purple: 'from-purple-500/10 to-purple-500/5 border-purple-500/20 text-purple-400',
    emerald: 'from-emerald-500/10 to-emerald-500/5 border-emerald-500/20 text-emerald-400',
    amber: 'from-amber-500/10 to-amber-500/5 border-amber-500/20 text-amber-400',
    red: 'from-red-500/10 to-red-500/5 border-red-500/20 text-red-400',
  };
  return (
    <div
      className={`p-5 rounded-2xl bg-gradient-to-br ${colors[color]} border backdrop-blur-sm ${className}`}
    >
      {children}
    </div>
  );
};

export const PrivacyPolicyPage: React.FC = () => {
  return (
    <LegalPageLayout title="Privacy Policy" lastUpdated="February 27, 2026" icon="shield">
      <section className="p-6 md:p-8 rounded-3xl bg-gradient-to-br from-zinc-900/60 to-zinc-800/40 border border-white/8 backdrop-blur-sm mb-8">
        <h2 className="text-white font-bold text-2xl md:text-3xl mb-4">Your Privacy Matters</h2>
        <p className="text-zinc-400 leading-relaxed text-base md:text-lg">
          This Privacy Policy explains how Musicade LLC (doing business as "Musicade," "we," "our,"
          or "us") collects, uses, shares, stores, and protects your personal information when you
          use our website, mobile applications, and related services (collectively, the "Platform").
        </p>
        <p className="text-zinc-400 leading-relaxed text-base md:text-lg mt-4">
          We process your personal information in accordance with applicable data protection laws,
          including the General Data Protection Regulation (GDPR), the California Consumer Privacy
          Act (CCPA), and other relevant privacy legislation.
        </p>
      </section>

      <Section number="1" title="Who We Are">
        <p>
          Musicade is an event creation and publishing platform that allows users to create,
          customize, publish, manage, and collaborate on events and related media content.
        </p>
        <ul className="space-y-2 list-disc pl-6 mt-4">
          <li>
            We act as the primary data controller for personal information collected directly
            through our Platform, except where third-party services independently process data under
            their own policies.
          </li>
          <li>
            Our services are currently operated in and primarily governed by the laws of the United
            States.
          </li>
        </ul>
      </Section>

      <Section number="2" title="What Info We Have and How We Get It">
        <ul className="space-y-3 list-none">
          <li>
            <strong className="text-white">Direct Collection:</strong> We collect information
            directly from you when you register an account, create events, upload media, add
            collaborators, purchase tickets, or communicate with us.
          </li>
          <li>
            <strong className="text-white">Automatic Collection:</strong> We automatically collect
            technical and usage data when you interact with the Platform, including device details,
            activity logs, and session information.
          </li>
          <li>
            <strong className="text-white">Third-Party Sources:</strong> We may receive information
            from third-party services you choose to use through Musicade, such as payment processors
            or analytics tools.
          </li>
          <li>
            <strong className="text-white">Generated Data:</strong> Certain information is generated
            through your usage patterns, preferences, and interactions with events, features, and
            content on the Platform.
          </li>
        </ul>
      </Section>

      <Section number="3" title="How We Use Your Information and Why">
        <p>
          We process your personal information only when we have a valid legal basis to do so.
          Depending on the specific processing activity, we rely on one or more of the following
          lawful bases:
        </p>
        <div className="space-y-4 mt-4">
          <HighlightBox color="blue">
            <p>
              <strong className="text-white">(a) Contract Performance:</strong> Processing necessary
              to fulfill our contractual obligations to you, including providing, operating, and
              maintaining your account and the services you request.
            </p>
          </HighlightBox>
          <HighlightBox color="purple">
            <p>
              <strong className="text-white">(b) Legitimate Interests:</strong> Processing necessary
              for our legitimate business interests—improving our services, ensuring platform
              security, preventing fraud, and conducting analytics.
            </p>
          </HighlightBox>
          <HighlightBox color="amber">
            <p>
              <strong className="text-white">(c) Legal Obligation:</strong> Processing necessary to
              comply with our legal and regulatory obligations, such as tax requirements, law
              enforcement requests, or other mandatory disclosures.
            </p>
          </HighlightBox>
          <HighlightBox color="emerald">
            <p>
              <strong className="text-white">(d) Consent:</strong> Processing based on your freely
              given, specific, informed, and unambiguous consent, which you may withdraw at any
              time—this applies to optional marketing communications, certain cookies, and other
              non-essential processing activities.
            </p>
          </HighlightBox>
        </div>
      </Section>

      <Section number="4" title="Personal Info Collected">
        <ul className="grid md:grid-cols-2 gap-4 list-none">
          <li className="p-4 rounded-xl bg-zinc-800/30 border border-white/5">
            <strong className="text-white block mb-1">Account Information</strong>
            <span className="text-zinc-400 text-sm">
              Name, email, phone, username, password, and optional profile details
            </span>
          </li>
          <li className="p-4 rounded-xl bg-zinc-800/30 border border-white/5">
            <strong className="text-white block mb-1">Event & Content Data</strong>
            <span className="text-zinc-400 text-sm">
              Event descriptions, media uploads, images, videos, and edits
            </span>
          </li>
          <li className="p-4 rounded-xl bg-zinc-800/30 border border-white/5">
            <strong className="text-white block mb-1">Transaction Information</strong>
            <span className="text-zinc-400 text-sm">
              Billing details and payment metadata (we don't store full card numbers)
            </span>
          </li>
          <li className="p-4 rounded-xl bg-zinc-800/30 border border-white/5">
            <strong className="text-white block mb-1">Usage & Device Information</strong>
            <span className="text-zinc-400 text-sm">
              IP address, device model, OS, browser, session duration, and interaction logs
            </span>
          </li>
          <li className="p-4 rounded-xl bg-zinc-800/30 border border-white/5">
            <strong className="text-white block mb-1">Communication Data</strong>
            <span className="text-zinc-400 text-sm">
              Messages, feedback, inquiries, and correspondence
            </span>
          </li>
          <li className="p-4 rounded-xl bg-zinc-800/30 border border-white/5">
            <strong className="text-white block mb-1">Preferences</strong>
            <span className="text-zinc-400 text-sm">
              Notification settings, marketing subscriptions, and engagement behavior
            </span>
          </li>
        </ul>
      </Section>

      <Section number="5" title="Who We Share Info To">
        <ul className="space-y-3 list-none">
          <li>
            <strong className="text-white">Service Providers:</strong> Trusted vendors that support
            our operations, such as hosting providers, analytics platforms, and payment processors
            like Stripe.
          </li>
          <li>
            <strong className="text-white">Legal and Regulatory Authorities:</strong> Where
            disclosure is required to comply with applicable laws, legal proceedings, or enforceable
            government requests.
          </li>
          <li>
            <strong className="text-white">Business Transfers:</strong> In the event of a merger or
            sale of assets, user information may be transferred to the successor entity.
          </li>
          <li>
            <strong className="text-white">Creators & Collaborators:</strong> Event-related
            information may be visible to authorized collaborators or participants.
          </li>
        </ul>
        <HighlightBox color="red" className="mt-4">
          <p className="text-red-300">
            <strong className="text-white">We do not sell</strong> personal information to third
            parties for their independent marketing or commercial purposes.
          </p>
        </HighlightBox>
      </Section>

      <Section number="6" title="Your Choices and Rights">
        <p>
          You may review, edit, or update your account information at any time through your profile
          settings. You can opt out of non-essential communications by adjusting your notification
          preferences.
        </p>
        <p className="mt-4">Where applicable by law, you may request:</p>
        <ul className="space-y-2 list-disc pl-6 mt-4">
          <li>Access to your personal information;</li>
          <li>Correction of inaccurate data;</li>
          <li>Deletion of your data; or</li>
          <li>Restriction of certain processing activities.</li>
        </ul>
        <p className="mt-4">
          To exercise these rights, submit a request through your account settings or email{' '}
          <a href="mailto:support@musicade.com" className="text-blue-400 hover:text-blue-300">
            support@musicade.com
          </a>
          .
        </p>
      </Section>

      <Section number="7" title="Data Retention and Protection">
        <p>
          We retain personal information only for as long as necessary to fulfill the purposes for
          which it was collected.
        </p>
        <ul className="grid md:grid-cols-2 gap-4 mt-4 list-none">
          <li className="p-4 rounded-xl bg-zinc-800/30 border border-white/5">
            <strong className="text-white">Account Information</strong>
            <p className="text-zinc-400 text-sm mt-1">
              Duration of active account + 3 years after closure
            </p>
          </li>
          <li className="p-4 rounded-xl bg-zinc-800/30 border border-white/5">
            <strong className="text-white">Transaction Information</strong>
            <p className="text-zinc-400 text-sm mt-1">7 years from transaction date</p>
          </li>
          <li className="p-4 rounded-xl bg-zinc-800/30 border border-white/5">
            <strong className="text-white">Usage & Device Information</strong>
            <p className="text-zinc-400 text-sm mt-1">2 years from collection</p>
          </li>
          <li className="p-4 rounded-xl bg-zinc-800/30 border border-white/5">
            <strong className="text-white">Communication Data</strong>
            <p className="text-zinc-400 text-sm mt-1">3 years from last correspondence</p>
          </li>
        </ul>
        <p className="mt-4">
          We implement robust technical, administrative, and organizational safeguards to protect
          your personal information.
        </p>
      </Section>

      <Section number="8" title="Not Intended for Children">
        <HighlightBox color="red">
          <p className="text-red-300">
            <strong className="text-white">Musicade is not intended for children under 13</strong>{' '}
            in the United States (COPPA compliant). We do not knowingly collect personal information
            from children under 13. Our service is generally restricted to users aged 16 and above.
          </p>
        </HighlightBox>
      </Section>

      <Section number="9" title="Looking After Your Info">
        <p>
          We take reasonable steps to ensure that the personal information we process is accurate,
          relevant, and limited to what is necessary for the intended purposes. Access to user data
          is restricted to authorized personnel.
        </p>
      </Section>

      <Section number="10" title="How and Where We Store Your Info">
        <p>
          Personal information is stored on secure servers operated either directly by Musicade or
          by authorized third-party cloud infrastructure providers. Data may be stored in the United
          States or other jurisdictions where our service providers maintain secure infrastructure.
        </p>
      </Section>

      <Section number="11" title="Contact Us">
        <p>
          If you have questions, concerns, or requests regarding this Privacy Policy or your
          personal information, you may contact us at:
        </p>
        <div className="p-4 rounded-2xl bg-zinc-800/40 border border-white/5 mt-4">
          <p className="text-white font-semibold">Musicade Legal & Privacy Team</p>
          <a
            href="mailto:support@musicade.com"
            className="text-blue-400 hover:text-blue-300 transition-colors"
          >
            support@musicade.com
          </a>
        </div>
      </Section>

      <Section number="12" title="Updates and Changes">
        <p>
          We may revise or update this Privacy Policy from time to time to reflect operational,
          legal, or feature-related changes. Material changes will be notified to all registered
          users via email at least 30 days before such changes take effect.
        </p>
      </Section>
    </LegalPageLayout>
  );
};
